// Interactive Cliff-Notes Training Manual (/training) & Guided Module Banner.
// Strictly adheres to AGENTS.md: explains UI/UX workflows and system guardrails only; never states,
// invents, or approximates underwriting rules, thresholds, rates, or pricing.
import { BookOpen, CheckCircle2, Circle, Compass, ExternalLink, Keyboard, Search, Sparkles } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useLocation } from "react-router";
import { Card, cx, Segmented, Tip } from "../components/ui";
import { HELP_LEVEL_OPTIONS, helpLevelStore, setHelpLevel, type HelpLevel } from "../lib/prefs";

export type TrainingRoleFilter = "ALL" | "AE" | "CA" | "AM" | "Processor" | "RM_Admin";

export interface TrainingDrillStep {
  id: string;
  action: string;
  detail: string;
}

export interface TrainingModule {
  id: string;
  number: number;
  title: string;
  route: string;
  externalReload?: boolean;
  mission: string;
  roles: Exclude<TrainingRoleFilter, "ALL">[];
  roleLabels: string[];
  drills: [TrainingDrillStep, TrainingDrillStep, TrainingDrillStep];
  guardrailTitle: string;
  guardrailText: string;
  keyHighlights: string[];
}

export const TRAINING_MODULES: readonly TrainingModule[] = [
  {
    id: "mod-1",
    number: 1,
    title: "Role Home, Navigation & Acting Seats",
    route: "/role",
    mission: "Start your day from your AI-prepared approval queue and switch workspaces or acting seats in one click.",
    roles: ["AE", "CA", "AM", "Processor", "RM_Admin"],
    roleLabels: ["AE", "CA", "AM", "Processor", "RM / Admin"],
    drills: [
      {
        id: "m1-1",
        action: "1. Pick Your Wall (Top Left)",
        detail: "Toggle between Prospecting (green header — zero borrower/deal PII) and Active deals (navy header — live loans, Desk, Triage Hub, Conditions).",
      },
      {
        id: "m1-2",
        action: "2. Clear 'Prepared for You'",
        detail: "Review items prepared by assistants and click Approve, Approve and send, or Decline.",
      },
      {
        id: "m1-3",
        action: "3. Switch Seat When Covering",
        detail: "Click the amber Acting as chip in the top bar to step into a granted seat, or click Stop acting in the banner to return to your own role.",
      },
    ],
    guardrailTitle: "System-of-Record & Separation of Duty",
    guardrailText: "No item is ever sent or decided without human approval, and nobody can approve their own proposal. Encompass remains the system of record for lending decisions.",
    keyHighlights: ["Workspace Wall Switcher", "Prepared for You Queue", "Audited Acting Role Banner"],
  },
  {
    id: "mod-2",
    number: 2,
    title: "Prospecting CRM & Broker Concierge",
    route: "/prospecting",
    mission: "Work your daily LO call block, launch pre-qual outreach playbooks, and generate 1-click Smart Drop links without mixing borrower PII into CRM.",
    roles: ["AE", "RM_Admin"],
    roleLabels: ["AE", "RM / Support"],
    drills: [
      {
        id: "m2-1",
        action: "1. Work Today & Relationships",
        detail: "Open /prospecting to clear due follow-ups (Done or Snooze +7d), or toggle Relationships between Table and Kanban views.",
      },
      {
        id: "m2-2",
        action: "2. Send an Email Smart Link",
        detail: "On any LO record (/prospecting/people/:id), click Smart Drop Link to copy a pre-bound /drop/:token or /pos/:token Email Smart Link.",
      },
      {
        id: "m2-3",
        action: "3. Launch LO Scenario Concierge",
        detail: "Click Open LO Scenario Concierge or select a Scenario Starter Playbook to draft outreach or jump into a linked Four-Legs Desk session.",
      },
    ],
    guardrailTitle: "D25 Privacy Wall (prospect_rw)",
    guardrailText: "Prospecting runs under a strict database wall that cannot read deal tables. Never type borrower names, SSNs, or loan numbers into CRM activity notes.",
    keyHighlights: ["Today's Call Block", "Table + Kanban Relationships", "1-Click LO Smart Drop Link"],
  },
  {
    id: "mod-3",
    number: 3,
    title: "Email Smart Link Portal, Co-Branded POS & Native E-Sign",
    route: "/pos/lsdrop_demo",
    mission: "Give Brokers/LOs, Processors, and Borrowers a Verified Email Smart Link Sign-In to drop multi-borrower files, complete a co-branded 1003 POS, run pre-UW diagnostics, and e-sign natively.",
    roles: ["AE", "AM", "Processor"],
    roleLabels: ["AE", "AM", "Processor", "External LO / Borrower"],
    drills: [
      {
        id: "m3-1",
        action: "1. Select Portal Workspace Tab",
        detail: "Switch between Smart Document Drop (/drop/:token), Co-Branded Retail POS (/pos/:token), and LS2 Native e-Signature & 1-Click LOE Studio (/sign/:token).",
      },
      {
        id: "m3-2",
        action: "2. Verify Pre-Underwriting Diagnostics",
        detail: "Inspect automatic Page Integrity badges (Verified Complete vs. Missing Pages) and 12-Month Bank Statement Continuity before uploading.",
      },
      {
        id: "m3-3",
        action: "3. Upload or Execute Native e-Signature",
        detail: "Click Upload & Auto-Route or sign a 1003 / LOE template with a deterministic SHA-256 ESIGN audit certificate.",
      },
    ],
    guardrailTitle: "Zero DocuSign Tax & Advisory Diagnostics",
    guardrailText: "Original uploaded files are always retained unaltered; pre-underwriting diagnostics are advisory until reviewed by assigned LendSure staff.",
    keyHighlights: ["6-Pillar Readiness Tracker", "12-Month Statement Continuity", "SHA-256 Native e-Signature Studio"],
  },
  {
    id: "mod-4",
    number: 4,
    title: "Smart Document Drop & Intelligent Triage Hub",
    route: "/smart-drops",
    mission: "Triage all incoming external and internal files in one place—auto-grouped by borrower, tagged with Laya 16-class taxonomy badges, and routed to AE, CA, AM, and Processor queues.",
    roles: ["AE", "CA", "AM", "Processor"],
    roleLabels: ["AE", "CA", "AM", "Processor"],
    drills: [
      {
        id: "m4-1",
        action: "1. Filter Your Seat Queue",
        detail: "Select AE Queue (1003/MISMO), CA Queue (Income/Credit/Assets), AM Queue (Conditions/LOEs), or Processor Queue (Appraisal/Title/HOI/Entity).",
      },
      {
        id: "m4-2",
        action: "2. Drop Internal Files or Create Scoped Links",
        detail: "Use the left card to drop files from your internal seat (AE/CA/AM/Processor), or the right card to generate a scoped external link.",
      },
      {
        id: "m4-3",
        action: "3. Bind to Loan / Condition or Request e-Sign",
        detail: "Verify Laya 16-class & Page Integrity badges on each borrower group, bind to a Loan or Condition ID, or trigger a 1-Click Native e-Sign / LOE request.",
      },
    ],
    guardrailTitle: "Laya Segmentation Hold Guardrail",
    guardrailText: "Any multi-document PDF flagged with Mixed Page · Segmentation Hold is held in the Processor queue for page splitting before underwriting review.",
    keyHighlights: ["Borrower Auto-Grouping", "Laya 16-Class Taxonomy", "Internal Multi-Role Dropzone"],
  },
  {
    id: "mod-5",
    number: 5,
    title: "Four-Legs Scenario Desk & 3-Option Smarter Fees",
    route: "/desk/",
    externalReload: true,
    mission: "Talk through a loan scenario in plain English, assess the Four Legs (Income, Credit, Equity, Assets), and present 3 real Core pricing options across 3 audience views.",
    roles: ["AE", "CA", "RM_Admin"],
    roleLabels: ["AE", "CA", "RM"],
    drills: [
      {
        id: "m5-1",
        action: "1. Pick LO-Linked vs. Practice Mode",
        detail: "Use the top flow banner to link the scenario to a Prospecting CRM LO (with 1-click Smart Drop link) or switch to Practice Scenario (Unsaved).",
      },
      {
        id: "m5-2",
        action: "2. Enter Scenario & State the 'Elephant'",
        detail: "Send scenario facts in the left chat composer (⌘Enter), state the 'Elephant in the room', and rate Income, Credit, Equity, and Assets on the Four legs tab.",
      },
      {
        id: "m5-3",
        action: "3. Present 3-View Pricing & Smarter Fees",
        detail: "Toggle between View 1 (LendSure Internal), View 2 (Broker/LO-Facing with Smarter Fees levers), and View 3 (Borrower Draft Fee LE).",
      },
    ],
    guardrailTitle: "Real Core Rules & Pricing Only",
    guardrailText: "All rates, points, and guideline checks come strictly from live LendSure Core / recorded pricing snapshots—never invent or approximate pricing or thresholds.",
    keyHighlights: ["Four-Legs Stool & Pendulum", "3-Option Target Revenue Curation", "Smarter Fees Levers (Broker & Borrower Views)"],
  },
  {
    id: "mod-6",
    number: 6,
    title: "Pipeline Grid & Unified Loan Drawer",
    route: "/pipeline",
    mission: "Manage every active loan by stage with spreadsheet-grade inline autosave, Excel paste, real-time presence, and a slide-over Loan Drawer.",
    roles: ["AE", "CA", "AM", "Processor", "RM_Admin"],
    roleLabels: ["AE", "CA", "AM", "Processor", "RM / Admin"],
    drills: [
      {
        id: "m6-1",
        action: "1. Filter by Scope, Month & Stage Strip",
        detail: "Switch between Mine, Team, All, or Assigned to me; click any Stage Pill in the strip to focus on a single stage.",
      },
      {
        id: "m6-2",
        action: "2. Edit Inline & Use Keyboard Stage Stepping",
        detail: "Type directly in any editable cell (autosaves immediately), paste ranges from Excel (⌘V), or press ] / [ to step a loan's stage.",
      },
      {
        id: "m6-3",
        action: "3. Open the Loan Drawer (Space)",
        detail: "Press Space on any row to inspect Details, Alerts, and History, assign pilot staff, or drop loan-scoped files in the embedded Smart Drop panel.",
      },
    ],
    guardrailTitle: "Inline Autosave & Audit Trail",
    guardrailText: "Every edit saves immediately and is recorded in the loan's Activity History so teammates can see every field change.",
    keyHighlights: ["Stage-Grouped Glide Grid", "Excel Copy/Paste & Fill-Down", "Slide-Over Loan Drawer + Smart Drop"],
  },
  {
    id: "mod-7",
    number: 7,
    title: "AM Conditions & AI Evidence Review",
    route: "/conditions",
    mission: "Clear underwriting conditions faster with page-level AI evidence citations while keeping the final clearing decision squarely in the Account Manager's hands.",
    roles: ["AM", "CA"],
    roleLabels: ["AM", "CA"],
    drills: [
      {
        id: "m7-1",
        action: "1. Triage the AM Worklist",
        detail: "Open /conditions to see loans ranked by oldest open condition, AI Clear recommendations, and items needing a person.",
      },
      {
        id: "m7-2",
        action: "2. Inspect Page-Level Citations",
        detail: "On /conditions/review/:conditionId, expand 'Why' and click cited document page links (e.g., page 4) to verify exact evidence in the PDF viewer.",
      },
      {
        id: "m7-3",
        action: "3. Request Scoped Stips or Record Decision",
        detail: "Use the bottom Smart Document Drop panel to send a condition-scoped link for missing stips, or click Clear / Not cleared / Ask.",
      },
    ],
    guardrailTitle: "AI Recommends — You Decide",
    guardrailText: "Model confidence is advisory only and cites specific document pages or structured facts; the Account Manager always makes the final call.",
    keyHighlights: ["AM Condition Worklist", "Page-Level PDF Citations", "Condition-Scoped Smart Drop Panel"],
  },
] as const;

export const KEYBOARD_SHORTCUTS: readonly { keys: string; where: string; action: string }[] = [
  { keys: "⌘K / Ctrl+K", where: "Everywhere", action: "Open Command Palette (search loans, LOs, modules, or toggle Tooltip Level)" },
  { keys: "Space", where: "Pipeline Grid (/pipeline)", action: "Open or close the right-hand Loan Drawer for the selected loan" },
  { keys: "] / [", where: "Pipeline Grid (/pipeline)", action: "Advance (]) or step back ([) the selected loan's stage" },
  { keys: "⌘V / Ctrl+V", where: "Pipeline Grid (/pipeline)", action: "Paste multi-cell values directly from Excel with preview" },
  { keys: "⌘Enter", where: "Four-Legs Desk (/desk/)", action: "Submit scenario facts or chat message to the Desk assistant" },
  { keys: "Esc", where: "Drawers & Modals", action: "Close open Loan Drawer, Command Palette, or Feedback Comment Mode" },
] as const;

const DRILLS_STORAGE_KEY = "ls.trainingDrills";

function readCompletedDrills(): string[] {
  try {
    const raw = localStorage.getItem(DRILLS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

function writeCompletedDrills(ids: string[]) {
  try {
    localStorage.setItem(DRILLS_STORAGE_KEY, JSON.stringify(ids));
  } catch {
    /* ignore private mode */
  }
}

/** Contextual 1-line Cliff-Notes strip shown at the top of pages when Help Level is set to "guided". */
export function GuidedModuleBanner() {
  const helpLevel = helpLevelStore.use();
  const { pathname } = useLocation();
  if (helpLevel !== "guided" || pathname === "/training") return null;

  const activeModule =
    TRAINING_MODULES.find((m) =>
      m.route === "/" ? pathname === "/" : pathname === m.route || pathname.startsWith(`${m.route}/`),
    ) ?? TRAINING_MODULES[0];

  return (
    <div
      role="region"
      aria-label="Guided speed-learn tip"
      className="flex flex-wrap items-center justify-between gap-2 border-b border-subtle bg-sunken px-3 py-1.5 text-[12px] text-ink-2 md:px-5"
    >
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1 rounded-full bg-accent px-2 py-0.5 text-[11px] font-bold text-on-accent">
          <Sparkles size={11} aria-hidden /> Guided Cliff Notes · Mod {activeModule.number}
        </span>
        <span className="font-semibold text-ink">{activeModule.title}:</span>
        <span className="truncate text-ink-2">
          {activeModule.drills.map((d) => d.action).join(" → ")}
        </span>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Link
          to="/training"
          className="focus-ring rounded px-2 py-0.5 font-semibold text-link underline-offset-2 hover:underline"
        >
          Open Full Manual
        </Link>
        <button
          type="button"
          onClick={() => setHelpLevel("standard")}
          className="focus-ring rounded border border-line bg-surface px-2 py-0.5 text-[11px] font-medium text-ink-2 hover:bg-hover hover:text-ink"
          title="Switch from Guided mode to Standard tooltips"
        >
          Switch to Standard
        </button>
      </div>
    </div>
  );
}

export function TrainingManualPage() {
  const helpLevel = helpLevelStore.use();
  const [roleFilter, setRoleFilter] = useState<TrainingRoleFilter>("ALL");
  const [query, setQuery] = useState("");
  const [completedDrills, setCompletedDrills] = useState<string[]>(() => readCompletedDrills());

  const totalDrillCount = TRAINING_MODULES.length * 3;
  const completedCount = completedDrills.length;

  const toggleDrill = (id: string) => {
    setCompletedDrills((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      writeCompletedDrills(next);
      return next;
    });
  };

  const resetProgress = () => {
    setCompletedDrills([]);
    writeCompletedDrills([]);
  };

  const filteredModules = useMemo(() => {
    const q = query.trim().toLowerCase();
    return TRAINING_MODULES.filter((m) => {
      if (roleFilter !== "ALL" && !m.roles.includes(roleFilter)) return false;
      if (!q) return true;
      const haystack = [
        m.title,
        m.mission,
        m.guardrailTitle,
        m.guardrailText,
        ...m.roleLabels,
        ...m.keyHighlights,
        ...m.drills.flatMap((d) => [d.action, d.detail]),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [roleFilter, query]);

  return (
    <div className="mx-auto max-w-[1200px] space-y-6 px-4 py-5 md:px-6">
      {/* Header & Interactive Tooltip Level Controls */}
      <header className="flex flex-wrap items-start justify-between gap-4 rounded-[12px] border border-subtle bg-surface p-5 shadow-[var(--shadow-1)]">
        <div className="max-w-2xl space-y-1.5">
          <div className="eyebrow flex items-center gap-1.5 text-accent">
            <BookOpen size={14} aria-hidden /> LS 2.0 v1 · Speed-Learning Cliff Notes
          </div>
          <h1 className="font-display text-[22px] leading-7 font-semibold text-ink">
            Master LS 2.0 in 15 Minutes (Scan → Do → Verify)
          </h1>
          <p className="text-[13px] leading-5 text-ink-2">
            Short, non-verbose, action-first modules for every seat. Pick your role below, check off each 3-step speed drill as you try it, and set your preferred <strong>Tooltip &amp; Help Level</strong> at any time.
          </p>
        </div>

        {/* Tooltip & Help Level Switcher Box */}
        <div className="flex flex-col gap-2 rounded-[10px] border border-line bg-sunken p-3.5">
          <div className="flex items-center justify-between gap-3">
            <span className="eyebrow flex items-center gap-1 text-ink-2">
              <Sparkles size={13} className="text-accent" aria-hidden /> Tooltips &amp; Help Level
            </span>
            <Tip
              content="This live badge previews your current Tooltip & Help Level setting."
              guide="In Guided mode, tooltips include extra Speed-Learn step hints and a contextual top bar."
              shortcut="⌘K"
            >
              <span
                tabIndex={0}
                className="focus-ring cursor-help rounded-full border border-line bg-surface px-2.5 py-0.5 text-[11px] font-semibold text-ink-2"
              >
                Hover to test tooltip
              </span>
            </Tip>
          </div>
          <Segmented<HelpLevel>
            label="Tooltips and help level"
            size="sm"
            value={helpLevel}
            onChange={(v) => setHelpLevel(v)}
            options={HELP_LEVEL_OPTIONS.map((o) => ({
              value: o.value,
              label: o.shortLabel,
              title: o.description,
            }))}
          />
          <div className="text-[11px] text-ink-3" data-testid="active-help-level-description">
            {HELP_LEVEL_OPTIONS.find((o) => o.value === helpLevel)?.description}
          </div>
        </div>
      </header>

      {/* Role Filter Bar, Search & Mastery Progress */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-[10px] border border-subtle bg-surface px-4 py-3">
        <div className="flex flex-wrap items-center gap-1.5" role="tablist" aria-label="Filter modules by role">
          {(
            [
              { key: "ALL", label: "All Modules (7)" },
              { key: "AE", label: "AE · Account Executive" },
              { key: "CA", label: "CA · Credit Analyst" },
              { key: "AM", label: "AM · Account Manager" },
              { key: "Processor", label: "Loan Processor" },
              { key: "RM_Admin", label: "RM / Admin" },
            ] as const
          ).map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={roleFilter === tab.key}
              onClick={() => setRoleFilter(tab.key)}
              className={cx(
                "focus-ring rounded-md px-3 py-1.5 text-[13px] font-semibold transition-colors",
                roleFilter === tab.key
                  ? "bg-primary text-on-primary"
                  : "bg-sunken text-ink-2 hover:bg-hover hover:text-ink",
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search size={14} className="pointer-events-none absolute top-1/2 left-2.5 -translate-y-1/2 text-ink-3" aria-hidden />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search modules, drills, hotkeys…"
              aria-label="Search training modules"
              className="focus-ring h-8 w-56 rounded-md border border-input bg-surface pr-2.5 pl-8 text-[13px] text-ink placeholder:text-ink-3"
            />
          </div>
          <div className="flex items-center gap-2 text-[12px] font-medium text-ink-2">
            <span className="tnum">
              Drills mastered: <strong>{completedCount}</strong>/{totalDrillCount}
            </span>
            {completedCount > 0 ? (
              <button
                type="button"
                onClick={resetProgress}
                className="focus-ring rounded px-1.5 py-0.5 text-[11px] text-ink-3 underline hover:text-ink"
              >
                Reset
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {/* 7 Modular Cliff-Notes Cards */}
      <div className="grid grid-cols-1 gap-4">
        {filteredModules.map((mod) => (
          <Card
            key={mod.id}
            title={
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded bg-primary px-2 py-0.5 text-[11px] font-bold text-on-primary">
                  MODULE {mod.number}
                </span>
                <span className="text-[14px] font-semibold tracking-normal text-ink normal-case">
                  {mod.title}
                </span>
                <code className="rounded bg-sunken px-1.5 py-0.5 font-mono text-[11px] font-normal text-ink-2 normal-case">
                  {mod.route}
                </code>
              </div>
            }
            action={
              <div className="flex flex-wrap items-center gap-2">
                <div className="hidden items-center gap-1 sm:flex">
                  {mod.roleLabels.map((r) => (
                    <span
                      key={r}
                      className="rounded-full border border-line bg-sunken px-2 py-0.5 text-[11px] font-semibold text-ink-2"
                    >
                      {r}
                    </span>
                  ))}
                </div>
                {mod.externalReload ? (
                  <a
                    href={mod.route}
                    className="focus-ring inline-flex h-8 items-center gap-1 rounded-md bg-primary px-2.5 text-[12px] font-semibold text-on-primary hover:bg-primary-hover"
                  >
                    Open Screen <ExternalLink size={12} aria-hidden />
                  </a>
                ) : (
                  <Link
                    to={mod.route}
                    className="focus-ring inline-flex h-8 items-center gap-1 rounded-md bg-primary px-2.5 text-[12px] font-semibold text-on-primary hover:bg-primary-hover"
                  >
                    Open Screen < Compass size={12} aria-hidden />
                  </Link>
                )}
              </div>
            }
          >
            <div className="space-y-4 p-4">
              {/* 1-Line Mission & Highlights */}
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-[14px] font-medium text-ink">
                  <span className="text-ink-3">1-Line Mission: </span>
                  {mod.mission}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {mod.keyHighlights.map((h) => (
                    <span
                      key={h}
                      className="rounded bg-selected px-2 py-0.5 text-[11px] font-medium text-link"
                    >
                      {h}
                    </span>
                  ))}
                </div>
              </div>

              {/* 3-Step Speed Drill */}
              <div className="grid grid-cols-1 gap-2.5 md:grid-cols-3">
                {mod.drills.map((drill) => {
                  const done = completedDrills.includes(drill.id);
                  return (
                    <button
                      key={drill.id}
                      type="button"
                      onClick={() => toggleDrill(drill.id)}
                      aria-pressed={done}
                      className={cx(
                        "focus-ring flex flex-col items-start gap-1.5 rounded-[8px] border p-3 text-left transition-colors",
                        done
                          ? "border-success/40 bg-success-bg/40"
                          : "border-subtle bg-sunken/60 hover:bg-hover",
                      )}
                    >
                      <div className="flex w-full items-center justify-between gap-2">
                        <span className="text-[13px] font-semibold text-ink">{drill.action}</span>
                        {done ? (
                          <CheckCircle2 size={15} className="shrink-0 text-success" aria-hidden />
                        ) : (
                          <Circle size={15} className="shrink-0 text-ink-3" aria-hidden />
                        )}
                      </div>
                      <p className="text-[12px] leading-4 text-ink-2">{drill.detail}</p>
                    </button>
                  );
                })}
              </div>

              {/* Guardrail Footer */}
              <div className="flex flex-wrap items-center gap-2 rounded-md border border-subtle bg-sunken px-3 py-2 text-[12px] text-ink-2">
                <span className="font-semibold text-ink">Guardrail · {mod.guardrailTitle}:</span>
                <span>{mod.guardrailText}</span>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Universal Keyboard Shortcuts Table */}
      <Card
        title={
          <span className="inline-flex items-center gap-1.5">
            <Keyboard size={14} aria-hidden /> Power Keyboard Shortcuts
          </span>
        }
      >
        <div className="overflow-x-auto">
          <table className="w-full text-left text-[13px]">
            <thead>
              <tr className="border-b border-subtle text-ink-3">
                <th className="px-4 py-2 font-semibold">Shortcut</th>
                <th className="px-4 py-2 font-semibold">Where It Works</th>
                <th className="px-4 py-2 font-semibold">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-subtle">
              {KEYBOARD_SHORTCUTS.map((s) => (
                <tr key={s.keys}>
                  <td className="px-4 py-2 font-mono font-semibold text-ink">{s.keys}</td>
                  <td className="px-4 py-2 text-ink-2">{s.where}</td>
                  <td className="px-4 py-2 text-ink">{s.action}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
